import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { redeemLoyaltyGift, searchAvailability } from '../apis/hotelApis';
import { colors, DateTimeFormats } from '../common/constants';
import { IHotelInfo, IHotelReservationInfo, ILoyaltyGiftOption, ILoyaltyReservationInfo } from '../types/hotelTypes';
import { PriceOptions } from '../types/bookingFormTypes';
import { getCheckoutDate, getDefaultDate } from '../utils/dateUtils';
import { getPageUrl } from '../utils/urlUtils';
import { formatCurrency } from '../utils/numberUtil';

const secondaryTextStyle: CSSProperties = {
  color: '#6b7280',
};

const cardBorderColor = '#eaded2';
const brandColor = colors.primary;
const softBrandBackground = '#fff5f5';

const rewardIconStyle: CSSProperties = {
  width: '1.5rem',
  height: '1.5rem',
  color: brandColor,
  flexShrink: 0,
};

interface LoyaltySummaryProps {
  className?: string;
  loyalty: ILoyaltyReservationInfo;
  hotel: IHotelInfo;
  reservation: IHotelReservationInfo;
  onRedeemed?: () => Promise<void> | void;
}

interface GiftActionProps {
  gift: ILoyaltyGiftOption;
  hotel: IHotelInfo;
  reservation: IHotelReservationInfo;
  onRedeemed?: () => Promise<void> | void;
}

const giftCardStyle: CSSProperties = {
  borderTop: `1px solid ${cardBorderColor}`,
  padding: '0.375rem 1rem',
};

const compactLabelStyle: CSSProperties = {
  color: '#8b7f77',
  fontSize: '0.875rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
};

const giftPointStyle: CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.75rem',
  height: '2.75rem',
  borderRadius: '0.75rem',
  background: softBrandBackground,
  color: brandColor,
  fontSize: '0.875rem',
  fontWeight: 700,
  lineHeight: 1.15,
  textAlign: 'center',
};

const pointLabelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 999,
  padding: '0.15rem 0.5rem',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.25,
};

const GiftAction = ({ gift, hotel, reservation, onRedeemed }: GiftActionProps) => {
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState<number>(0);
  const [overnightFrom, setOvernightFrom] = useState(
    DateTime.fromJSDate(
      getDefaultDate(undefined, {
        priceOption: PriceOptions.Night,
        overnightTimeFrom: hotel.overnightTimeFrom,
        overnightTimeDefault: hotel.overnightTimeDefault,
      }),
    ).toFormat("yyyy-MM-dd'T'HH:mm"),
  );
  const [options, setOptions] = useState<Array<{ roomTypeId: number; roomTypeName: string; totalPrice: number }>>([]);
  const [isLoadingOptions, setLoadingOptions] = useState(false);
  const [isRedeeming, setRedeeming] = useState(false);
  const [isReviewing, setReviewing] = useState(false);
  const [error, setError] = useState('');
  const [createdReservationId, setCreatedReservationId] = useState<number | undefined>();

  const isUpgrade = gift.actionType === 'ROOM_UPGRADE';
  const isOvernight = gift.actionType === 'FREE_OVERNIGHT_BOOKING';
  const isDisabledByReason = Boolean(gift.reason);
  const currentRoomRank = hotel.rooms.find((room) => room.roomTypeId === reservation.RoomTypeId)?.roomUpgradeRank;
  const nextRoomRank = Math.max(
    0,
    ...hotel.rooms
      .map((room) => Number(room.roomUpgradeRank))
      .filter((rank) => Boolean(currentRoomRank) && rank > 0 && rank < Number(currentRoomRank)),
  );

  useEffect(() => {
    if (!isReviewing || (!isUpgrade && !isOvernight)) {
      return;
    }

    let isMounted = true;
    setLoadingOptions(true);
    setError('');

    const loadOptions = async () => {
      try {
        const from = isOvernight
          ? DateTime.fromFormat(overnightFrom, "yyyy-MM-dd'T'HH:mm")
          : DateTime.fromISO(reservation.ArrivalDate);
        const to = isOvernight
          ? getCheckoutDate(from, {
              priceOption: PriceOptions.Night,
              overnightTimeFrom: hotel.overnightTimeFrom,
            })
          : DateTime.fromISO(reservation.DepartureDate);
        const data = await searchAvailability(hotel.hotelId, {
          from: from.toFormat(DateTimeFormats.GENERIC_DATE_FORMAT),
          to: to.toFormat(DateTimeFormats.GENERIC_DATE_FORMAT),
          priceOption: isOvernight ? PriceOptions.Night : reservation.PriceOption,
        });
        const mapped = data
          .filter((item) => {
            if (item.AvailableRooms <= 0) {
              return false;
            }
            if (isUpgrade) {
              if (gift.eligibleRoomTypeIds?.length) {
                return gift.eligibleRoomTypeIds.includes(item.RoomTypeId);
              }
              const targetRank = hotel.rooms.find((room) => room.roomTypeId === item.RoomTypeId)?.roomUpgradeRank;
              return Boolean(nextRoomRank && targetRank === nextRoomRank);
            }
            if (gift.eligibleRoomTypeIds?.length) {
              return gift.eligibleRoomTypeIds.includes(item.RoomTypeId);
            }
            return true;
          })
          .map((item) => {
            const room = hotel.rooms.find((candidate) => candidate.roomTypeId === item.RoomTypeId);
            return {
              roomTypeId: item.RoomTypeId,
              roomTypeName: room?.roomTypeName || item.RoomTypeName || `Room ${item.RoomTypeId}`,
              totalPrice: item.TotalPrice,
            };
          });

        if (!isMounted) {
          return;
        }
        setOptions(mapped);
        setSelectedRoomTypeId(mapped[0]?.roomTypeId || 0);
      } catch (err) {
        console.error(err);
        if (isMounted) {
          setError('Không tải được danh sách phòng áp dụng.');
        }
      } finally {
        if (isMounted) {
          setLoadingOptions(false);
        }
      }
    };

    loadOptions();
    return () => {
      isMounted = false;
    };
  }, [gift, hotel, isOvernight, isReviewing, isUpgrade, nextRoomRank, overnightFrom, reservation]);

  const extendedCheckoutLabel = useMemo(() => {
    if (gift.actionType !== 'EXTEND_CHECKOUT' || !gift.extensionHours) {
      return '';
    }
    return DateTime.fromISO(reservation.DepartureDate)
      .plus({ hours: gift.extensionHours })
      .toFormat(DateTimeFormats.DEFAULT);
  }, [gift, reservation.DepartureDate]);

  const handleRedeem = async () => {
    setRedeeming(true);
    setError('');
    try {
      const response = await redeemLoyaltyGift(hotel.hotelId, reservation.ReservationRoomId, {
        thresholdPoints: gift.thresholdPoints,
        actionType: gift.actionType,
        targetRoomTypeId: isUpgrade || isOvernight ? selectedRoomTypeId : undefined,
        from: isOvernight
          ? DateTime.fromFormat(overnightFrom, "yyyy-MM-dd'T'HH:mm").toFormat(DateTimeFormats.GENERIC_DATE_FORMAT)
          : undefined,
      });
      setCreatedReservationId(response.createdReservationId);
      await onRedeemed?.();
    } catch (err) {
      console.error(err);
      setError('Không thể đổi quà lúc này. Vui lòng thử lại hoặc liên hệ khách sạn.');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div style={giftCardStyle}>
      <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'center', minHeight: '3rem' }}>
        <span style={giftPointStyle}>
          <span style={{ fontSize: '1.125rem', lineHeight: 1.1 }}>{gift.thresholdPoints}</span>
          <span style={{ fontSize: '0.75rem', lineHeight: 1.1 }}>điểm</span>
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#4b5563', fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.25 }}>{gift.title}</div>
          {gift.description && (
            <div style={{ ...secondaryTextStyle, fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.25 }}>
              {gift.description}
            </div>
          )}
        </div>
        {gift.reason ? (
          <span
            style={{
              borderRadius: 999,
              background: '#f3f4f6',
              color: '#6b7280',
              padding: '0.3rem 0.65rem',
              fontSize: '0.875rem',
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {gift.reason}
          </span>
        ) : !isReviewing ? (
          <Button
            size="sm"
            style={{
              minWidth: '3.25rem',
              borderRadius: 999,
              background: brandColor,
              borderColor: brandColor,
              fontSize: '0.875rem',
              fontWeight: 700,
              flexShrink: 0,
            }}
            disabled={isRedeeming}
            onClick={() => setReviewing(true)}
          >
            Đổi
          </Button>
        ) : null}
      </div>
      {isReviewing && !isDisabledByReason && (
        <div style={{ marginTop: '0.625rem', padding: '0.625rem', borderRadius: '0.5rem', background: '#faf7f4' }}>
          {gift.actionType === 'EXTEND_CHECKOUT' && (
            <div style={{ ...secondaryTextStyle, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              Trả phòng mới: <b>{extendedCheckoutLabel}</b>
            </div>
          )}
          {isOvernight && (
            <Form.Group style={{ marginBottom: '0.5rem' }}>
              <Form.Label style={{ fontSize: '0.875rem' }}>Ngày nhận phòng</Form.Label>
              <Form.Control
                type="datetime-local"
                value={overnightFrom}
                onChange={(event) => setOvernightFrom(event.target.value)}
              />
            </Form.Group>
          )}
          {isUpgrade && options.length === 1 && !isLoadingOptions ? (
            <div style={{ ...secondaryTextStyle, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              Nâng cấp lên: <b>{options[0].roomTypeName}</b>
            </div>
          ) : null}
          {(isOvernight || (isUpgrade && (isLoadingOptions || options.length !== 1))) && (
            <Form.Group style={{ marginBottom: '0.5rem' }}>
              <Form.Label style={{ fontSize: '0.875rem' }}>Loại phòng</Form.Label>
              <Form.Select
                disabled={isLoadingOptions || options.length === 0}
                value={selectedRoomTypeId}
                onChange={(event) => setSelectedRoomTypeId(Number(event.target.value))}
              >
                {isLoadingOptions && <option>Đang tải...</option>}
                {!isLoadingOptions && options.length === 0 && <option>Không có phòng phù hợp</option>}
                {options.map((option) => (
                  <option key={option.roomTypeId} value={option.roomTypeId}>
                    {option.roomTypeName}
                    {isUpgrade ? ` - ${formatCurrency(option.totalPrice)}` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button
              size="sm"
              variant="link"
              style={{ color: '#6b7280', textDecoration: 'none' }}
              onClick={() => setReviewing(false)}
            >
              Huỷ
            </Button>
            <Button
              size="sm"
              style={{ background: brandColor, borderColor: brandColor, fontWeight: 700 }}
              disabled={isRedeeming || isLoadingOptions || ((isUpgrade || isOvernight) && !selectedRoomTypeId)}
              onClick={handleRedeem}
            >
              {isRedeeming ? <Spinner size="sm" animation="border" /> : 'Xác nhận đổi'}
            </Button>
          </div>
        </div>
      )}
      {error && (
        <Alert style={{ marginTop: '0.5rem', marginBottom: 0 }} variant="danger">
          {error}
        </Alert>
      )}
      {createdReservationId && (
        <Alert style={{ marginTop: '0.5rem', marginBottom: 0 }} variant="success">
          Đã tạo đặt phòng miễn phí.{' '}
          <a href={getPageUrl(`/${hotel.hotelId}/reservation/${createdReservationId}`)}>Xem đặt phòng</a>
        </Alert>
      )}
    </div>
  );
};

export const LoyaltySummary = ({ className, loyalty, hotel, reservation, onRedeemed }: LoyaltySummaryProps) => {
  const redeemableGifts = loyalty.redeemableGifts || [];

  return (
    <div
      style={{
        marginBottom: '1rem',
        padding: 0,
        borderRadius: '0.75rem',
        border: `1px solid ${cardBorderColor}`,
        background: '#fff',
        overflow: 'hidden',
        fontSize: '1rem',
      }}
      className={className}
    >
      <div style={{ padding: '0.875rem 1rem', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <span
              style={{
                width: '3.25rem',
                height: '3.25rem',
                borderRadius: '1.625rem',
                background: softBrandBackground,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '0.625rem',
                flexShrink: 0,
              }}
            >
              <svg
                style={{ ...rewardIconStyle, marginRight: 0, color: brandColor, fill: brandColor }}
                focusable="false"
                aria-hidden="true"
                viewBox="0 0 24 24"
                data-testid="CardGiftcardIcon"
              >
                <path d="M20 7h-2.18A3 3 0 0 0 12 5.5 3 3 0 0 0 6.18 7H4a1 1 0 0 0-1 1v3h2v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9h2V8a1 1 0 0 0-1-1zm-5-1.5A1.5 1.5 0 0 1 16.5 7H13V5.5zM7.5 7A1.5 1.5 0 0 1 9 5.5V7H7.5zM5 9h6v2H5V9zm2 4h4v6H7v-6zm6 6v-6h4v6h-4zm6-8h-6V9h6v2z" />
              </svg>
            </span>
            <div style={{ minWidth: 0 }}>
              <strong style={{ display: 'block', fontSize: '1.3rem', fontWeight: 500, lineHeight: 1.2 }}>
                Khách hàng thân thiết
              </strong>
              {loyalty.estimatedPoints > 0 && (
                <span
                  style={{
                    ...pointLabelStyle,
                    background: '#fff3cd',
                    color: '#7a5b00',
                    marginTop: '0.25rem',
                  }}
                >
                  +{loyalty.estimatedPoints} điểm sau khi trả phòng
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ color: brandColor, fontSize: '1.375rem', fontWeight: 700, lineHeight: 1 }}>
              {loyalty.currentPoints}
            </div>
            <div style={compactLabelStyle}>ĐIỂM</div>
          </div>
        </div>
      </div>
      {redeemableGifts.length > 0 && (
        <div>
          <div
            style={{
              ...compactLabelStyle,
              padding: '0.5rem 1rem',
              background: '#faf5f2',
              borderTop: `1px solid ${cardBorderColor}`,
            }}
          >
            Đổi điểm lấy quà
          </div>
          {redeemableGifts.map((gift) => (
            <GiftAction
              key={`${gift.actionType}-${gift.thresholdPoints}`}
              gift={gift}
              hotel={hotel}
              reservation={reservation}
              onRedeemed={onRedeemed}
            />
          ))}
        </div>
      )}
    </div>
  );
};
